import type { QueryClient } from "@tanstack/react-query";

import { gridQuery, isBadAsOf, isNoGrid, type Basis, type ChangeDate, type GridResponse, type Market, type RowChange, type UpcomingFee } from "./api";
import { ABSENT, COMPONENT_LABEL, countryName, humanDate, latestIso, MARKET_LABEL, money, vatPct } from "./format";

// The as-of date of the Price Run (C1 v1.24, companion of precision-pricing c4-as-of-grid: C4 v1.6, C2 v1.26). The URL names a
// date, C2 resolves it to the set in force on it, and every amount, rate and margin shown is C2's string (H12): the browser
// works out no figure, only which date it asked for and which words go with it.

/** Twelve months, as C2 accepts (`AS_OF_HORIZON_DAYS`): the picker's upper bound. C2 refuses a later date by name. */
export const AS_OF_HORIZON_DAYS = 366;

/** A real calendar date `YYYY-MM-DD` from the URL, else null (today). The range is C2's to judge: a date after the horizon
 * answers 422 `bad_as_of`, and a date on or before the run date answers today's set, which `pickedDate` reads as today. */
export function asOfFromSearch(value: unknown): string | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  try {
    return new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value ? value : null;
  } catch {
    return null;
  }
}

/** The ISO date `days` after another: a calendar step for the picker's bound, never a figure. */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whether a date is within C2's range: after the run date and at most twelve months on (C2 refuses a later one). */
export function withinHorizon(date: string, runDate: string | null): boolean {
  return runDate === null || (date > runDate && date <= addDays(runDate, AS_OF_HORIZON_DAYS));
}

/** How long the date field's value rests before it is read, as the New price box's typed price: a date typed part by part
 * is read once, when it is whole (review round 2). */
export const AS_OF_SETTLE_MS = 400;

/** What the date field's value asks for: null (today) on the run date, the date when it is whole and within C2's range, and
 * undefined (nothing) for anything else. In the browser every part typed or emptied changes the value: a year typed digit by
 * digit passes through 0002 and 0020, a day through the 1st, and an emptied part empties the whole value; none of these may
 * write the URL or read a grid, so an empty field is nothing, and Back to today is the way back (review rounds 1 and 2). */
export function fieldDate(value: string, runDate: string | null): string | null | undefined {
  if (runDate !== null && value === runDate) return null;
  return asOfFromSearch(value) !== null && withinHorizon(value, runDate) ? value : undefined;
}

/** The run date of the grids on screen: the date C2 reads the COGS on (`cogs_as_of`, the run's), else, on an API before
 * C2 v1.26, the grid's own date. */
export function runDateOf(grids: GridResponse[]): string | null {
  return latestIso(grids.map((g) => g.envelope.cogs_as_of ?? g.grid.as_of));
}

/** The date the figures on screen are for: the date C2 echoes when it is after the run date, else null (today). A date on or
 * before the run date reads today's set (C2 debt D5: the past is not served), so it is shown as today, never as a past date. */
export function pickedDate(grids: GridResponse[], runDate: string | null): string | null {
  const asked = latestIso(grids.map((g) => g.envelope.as_of));
  return asked !== null && runDate !== null && asked > runDate ? asked : null;
}

/** The URL named a date but C2 answered today's set (a date on or before the run date, or an API before C2 v1.26): the date
 * is dropped from the URL, so a shared link never claims a date its screen does not show (review round 1). */
export function staleAsOf(asked: string | null, grids: GridResponse[], runDate: string | null): boolean {
  return asked !== null && grids.length > 0 && pickedDate(grids, runDate) === null;
}

/** The change dates of the newest run on screen: markets can sit on different runs when one market's newest set failed, so the
 * list is the envelope's with the highest run id (review round 1). Undefined on an API before C2 v1.26. */
export function changeDatesOf(grids: GridResponse[]): ChangeDate[] | undefined {
  const newest = grids.reduce<GridResponse | null>((best, g) => (best === null || g.envelope.grid_run_id > best.envelope.grid_run_id ? g : best), null);
  return newest?.envelope.change_dates;
}

/** The date of the set in force on the picked date, when a change is in force by then; null when the set is today's. */
export function inForceOf(grids: GridResponse[], runDate: string | null): string | null {
  const from = latestIso(grids.map((g) => g.envelope.in_force_from));
  return from !== null && runDate !== null && from > runDate ? from : null;
}

/** The day a dated evaluation or view answered on, when it is not the date on screen (C1 v1.25). C2 v1.27 echoes the day its fees
 * are for: the picked date after C2's own today, and C2's today on or before it; an API before v1.27 echoes today on any date.
 * Null on today's figures, while nothing has answered, and when every answer read the date on screen. The browser compares ISO
 * dates only; a market C2 does not judge (`as_of` null) says nothing. */
export function otherDay(echoes: Array<string | null | undefined>, asOf: string | null): string | null {
  return asOf === null ? null : latestIso(echoes.filter((day) => day !== asOf));
}

const SOURCE_WORDS: Array<[keyof ChangeDate["sources"], string, string]> = [
  ["fee_rate", "fee rate", "fee rates"], ["fulfilment_future", "fulfilment projection", "fulfilment projections"], ["projected_cogs", "projected COGS", "projected COGS"],
];
/** One "Figures move on" link: "15 Oct 2026 · fee rate", "1 Nov 2026 · 2 fulfilment projections". */
export function changeDateLabel(entry: ChangeDate): string {
  const parts = SOURCE_WORDS.filter(([key]) => entry.sources[key] > 0).map(([key, one, many]) => (entry.sources[key] === 1 ? one : `${entry.sources[key]} ${many}`));
  return `${humanDate(entry.date)} · ${parts.length > 0 ? parts.join(", ") : AS_OF_COPY.noSource}`;
}

/** Why a component moves: the fee-rate registry version, or PFC's note on its projection. */
export function changeSource(c: RowChange): string {
  if (c.source === "fee_rate") return c.note == null ? AS_OF_COPY.feeRate : AS_OF_COPY.feeRateVersion(String(c.note));
  return c.note ? AS_OF_COPY.projected(String(c.note)) : AS_OF_COPY.projectedBare;
}

/** What one component does on the date, both figures as C2 wrote them: "FBA fee per unit: €3.12 to €4.00 from 15 Oct 2026", a
 * store's with its country ("Courier, Spain: ..."). In a projection (`rates`) a commission or DSF change carries the registry
 * rate as a fraction, so it reads as a percentage; every other value is an amount in `currency`. */
export function changeText(c: RowChange, currency: string | null, rates = false): string {
  const isRate = rates && (c.component === "referral" || c.component === "dsf");
  const figure = (value: string | null) => (value == null ? ABSENT : isRate ? vatPct(value) : money(value, currency));
  const label = `${COMPONENT_LABEL[c.component]}${isRate ? " rate" : ""}${c.country ? `, ${countryName(c.country)}` : ""}`;
  return `${label}: ${figure(c.today)} to ${figure(c.as_of)} ${AS_OF_COPY.from(humanDate(c.effective_from))}`;
}

/** The next fee change of an Amazon row (C4 v1.5 `upcoming_fee_change`, c7-v1-6 debt D5): "FBA fee per unit €4.00 from
 * 15 Oct 2026", or a rate as a percentage: "Commission rate 18% from 29 Nov 2026". */
export function upcomingText(u: UpcomingFee): string {
  const fixed = u.rate_type === "fixed";
  return `${COMPONENT_LABEL[u.component]}${fixed ? "" : " rate"} ${fixed ? money(u.value, u.currency) : vatPct(u.value)} ${AS_OF_COPY.from(humanDate(u.effective_from))}`;
}

/** The new figure of a fee change, for the drawer's component line (C1 v1.28): an amount as money, a rate as "rate 18%". */
export function upcomingValue(u: UpcomingFee): string {
  return u.rate_type === "fixed" ? money(u.value, u.currency) : `rate ${vatPct(u.value)}`;
}

/** The drawer notice's sentence (C1 v1.28): an amount names today's figure from the row's own component beside the new one,
 * "FBA fee per unit: €3.12 today, €4.00 from 15 Oct 2026."; a rate names the new rate only, since the row carries today's
 * fee as an amount and not its rate, and the browser works out no rate from it. */
export function noticeText(u: UpcomingFee, today: string | null, currency: string | null): string {
  if (u.rate_type !== "fixed") return AS_OF_COPY.noticeRate(upcomingText(u));
  return AS_OF_COPY.noticeFixed(COMPONENT_LABEL[u.component], today == null ? AS_OF_COPY.noCost : money(today, currency), money(u.value, u.currency), humanDate(u.effective_from));
}

/** What a pick from the drawer came to: the date written, the date unread (written nothing), or dropped (written nothing). */
export type PickOutcome = "picked" | "unread" | "dropped";
/** The drawer's pick (C1 v1.28): a date, or null for today, and whether the drawer that asked is still open. */
export type PickFn = (date: string | null, wanted: () => boolean) => Promise<PickOutcome>;

/** The drawer's date pick (C1 v1.28): the grid of every market on screen for the date (null: today) is read first and the date
 * written after, so the open sheet goes from one date's figures straight to the other's and never closes while they load (the
 * grid read keeps no placeholder, so a date written first empties the rows the sheet finds its row in). The date is written only
 * while the drawer that asked is open (`wanted`, review round 1: a late write would restore the row it was pressed on), and only
 * when every read answered, or C2 named the market as having no grid or refused the date, both of which the Price Run shows as
 * it does for a date picked in the toolbar; a read that failed writes nothing and the drawer says so. Never throws. */
export async function readThenPick(client: QueryClient, markets: Market[], basis: Basis, date: string | null, wanted: () => boolean,
  pick: (date: string | null) => void): Promise<PickOutcome> {
  await Promise.all(markets.map((market) => client.prefetchQuery(gridQuery(market, basis, date))));
  if (!wanted()) return "dropped";
  const unread = markets.some((market) => {
    const state = client.getQueryState(gridQuery(market, basis, date).queryKey);
    return state?.status !== "success" && !isNoGrid(state?.error) && !isBadAsOf(state?.error);
  });
  if (unread) return "unread";
  pick(date);
  return "picked";
}

/** The banner's sentence on the rows that move: per market on screen, from C2's `changed_rows` (never a store row). */
export function movedLine(grids: GridResponse[]): string {
  const counts = grids.filter((g) => g.grid.changed_rows !== undefined).map((g) => [MARKET_LABEL[g.grid.market], g.grid.changed_rows ?? 0] as const);
  if (counts.every(([, n]) => n === 0)) return AS_OF_COPY.noneMove;
  return AS_OF_COPY.move(counts.filter(([, n]) => n > 0).map(([market, n]) => `${market} ${n}`).join(", "));
}

export const AS_OF_COPY = {
  label: "As of",
  today: "today",
  pickTitle: "Pick a date up to twelve months ahead. The figures are the ones the daily run computed for the fees in force on that date.",
  backToToday: "Back to today",
  inForce: (date: string) => `in force from ${date}`,
  noneInForce: "no dated change in force yet",
  movesOn: "Figures move on:",
  noSource: "a change",
  noDates: "No fee or fulfilment change is dated in the next twelve months, so every date reads today's figures.",
  from: (date: string) => `from ${date}`,
  feeRate: "fee rate",
  feeRateVersion: (version: string) => `fee rate version ${version}`,
  projected: (note: string) => `projected by PFC: ${note}`,
  projectedBare: "projected by PFC",
  bannerHead: (asOf: string, from: string | null) => (from
    ? `Figures as of ${asOf}: the fees in force from ${from}.`
    : `Figures as of ${asOf}: no dated change is in force yet, so every figure is today's.`),
  move: (list: string) => `Rows that differ from today (Amazon marketplaces and Shopify ship countries; a store shows its countries'): ${list}.`,
  noneMove: "No row on screen differs from today.",
  bannerCogs: (runDate: string) => `COGS stay as read on ${runDate} until Projected COGS ships.`,
  chipsOn: (day: string) => `The rules chips are judged at the fees of ${day}.`,
  viewOn: (label: string, day: string) => `The ${label} view reads the fees of ${day}.`,
  incomplete: (market: string, dates: string) => `The last run could not compute the ${market} figures for ${dates}, so the set in force before that is shown.`,
  badDate: "There are no figures for that date: pick a date from today to twelve months ahead.",
  moves: "Moves",
  more: (n: number) => `${n} more`,
  upcoming: (date: string) => `Fee change ${date}`,
  upcomingTitle: (text: string) => `${text}. Pick this date to see the figures.`,
  componentsOn: (date: string) => `Fulfilment fee components in force on ${date}`,
  componentChange: (today: string, from: string, source: string) => `today ${today}, in force ${from} · ${source}`,
  noticeLabel: "Coming fee change",
  noticeTitle: (date: string) => `Fee change on ${date}`,
  noticeFixed: (label: string, today: string, next: string, date: string) => `${label}: ${today} today, ${next} from ${date}.`,
  noticeRate: (text: string) => `${text}.`,
  noCost: "no cost yet",
  noticeToday: "The figures below are today's.",
  noticeFar: "That date is more than twelve months ahead, so its figures are not served yet. The figures below are today's.",
  noticeSee: (date: string) => `See the figures on ${date}`,
  noticeReading: (date: string) => `Reading the figures on ${date}…`,
  unreadDate: (date: string) => `The figures on ${date} cannot be read right now. Try again in a moment.`,
  drawerLabel: "Date on screen",
  drawerOn: (date: string) => `Figures as of ${date}.`,
  backReading: "Reading today's figures…",
  unreadToday: "Today's figures cannot be read right now. Try again in a moment.",
  arrow: (next: string, date: string) => `→ ${next} from ${date}`,
  feeDayLabel: (date: string) => `Margins on ${date}`,
  feeDayHead: (date: string) => `On ${date}, with the fees in force then`,
  feeDayMoves: (fulfilToday: string, fulfilOn: string, feesToday: string, feesOn: string) => `Fulfil ${fulfilToday} → ${fulfilOn} · Fees ${feesToday} → ${feesOn}`,
  feeDayPending: (date: string) => `Working out the margins on ${date}…`,
  feeDayUnavailable: (date: string) => `The margins on ${date} cannot be worked out right now. The figures above are today's.`,
  cogsNote: (runDate: string, asOf: string) => `COGS as read on ${runDate}: the figures of ${asOf} move the fees only, until Projected COGS ships.`,
  boxOn: (date: string) => ` on ${date}`,
  boxChanges: (date: string, list: string) => `Fees in force on ${date}: ${list}.`,
  boxNoChanges: (date: string) => `No fee differs from today's on ${date}.`,
  boxRules: (date: string) => `Rules judged with the fees in force on ${date}.`,
  judgedOn: (day: string) => `Judged at the fees of ${day}.`,
  commitNote: "Prices commit as of today: the date picked above changes the figures shown, not when a price goes live.",
};
